import type { ReactNode } from "react";

import { Trash2 } from "lucide-react";

import { ButtonV4, ChipV4, ModalV4 } from "../common/ui";
import formatComma from "react-lib/utils/formatComma";
import type { SetEntry } from "../common/sequence/OrderSheetV4";

import { CATEGORY_LABELS, CATEGORY_TONES, formatLabSummary, type PickItem } from "./ordersModelV4";

// Types
export type SetTab = "all" | "mine" | "clinic";

type ModalOrderSetV4Props = {
  // data
  panel: "sets" | "favs";
  setTab: SetTab;
  setRows: Record<string, any>[];
  openSetId: number | null;
  setExcluded: Record<string, boolean>;
  favourites: PickItem[];
  /** ช่องค้นหา + ผลค้นหาสำหรับเพิ่มรายการโปรด */
  favPicker: ReactNode;
  setEntriesOf: (id: number) => SetEntry[];
  // callback
  onClose: () => void;
  onChangeTab: (tab: SetTab) => void;
  onToggleSet: (id: number) => void;
  onChangeExcluded: (excluded: Record<string, boolean>) => void;
  onRemoveSetEntry: (id: number, key: string) => void;
  onAddSet: (id: number, name: string) => void;
  onAddFromFavourite: (pick: PickItem) => void;
  onRemoveFavourite: (pick: PickItem) => void;
};

// Const
export const SET_TABS: [SetTab, string][] = [
  ["all", "ทั้งหมด"],
  ["mine", "ของฉัน"],
  ["clinic", "ของคลินิก"],
];

/** Order Set / รายการโปรด ของใบคำสั่ง */
const ModalOrderSetV4 = (props: ModalOrderSetV4Props) => {
  const { setExcluded } = props;

  // Render
  const renderSet = (item: Record<string, any>) => {
    const entries = props.setEntriesOf(item.id);
    const categories = [...new Set(entries.map((entry) => entry.kind))];
    const chosen = entries.filter((entry) => !setExcluded[entry.key]);
    const isOpen = props.openSetId === item.id;
    const total = entries.reduce((sum, entry) => sum + entry.price, 0);

    return (
      <div key={item.id} className={`hv4-ord-set${isOpen ? " hv4-ord-set-open" : ""}`}>
        <div className="hv4-ord-set-row">
          <div className="hv4-ord-set-info">
            <div className="hv4-ord-set-title">
              <b className="hv4-ord-set-name">{item.name}</b>
              <ChipV4 size="sm" bold tone={item.type === "P" ? "expire" : "grey"}>
                {item.type === "P" ? "ของฉัน" : "ของคลินิก"}
              </ChipV4>
            </div>
            <div className="hv4-ord-set-meta">
              {categories.map((category) => (
                <ChipV4 key={category} size="sm" tone={CATEGORY_TONES[category]}>
                  {CATEGORY_LABELS[category]}
                </ChipV4>
              ))}
              <span>
                {entries.length} รายการ · {formatComma(total, "en-US", false)} ฿
              </span>
            </div>
          </div>
          <ButtonV4
            variant={isOpen ? "ghost" : "primary"}
            aria-expanded={isOpen}
            onClick={() => props.onToggleSet(item.id)}
          >
            {isOpen ? "ปิด" : "เลือกรายการในชุด"}
          </ButtonV4>
        </div>
        {isOpen && (
          <div className="hv4-ord-set-items">
            {entries.map((entry) => (
              <div key={entry.key} className="hv4-ord-set-item">
                <label className="hv4-ord-set-check">
                  <input
                    type="checkbox"
                    checked={!setExcluded[entry.key]}
                    onChange={() =>
                      props.onChangeExcluded({
                        ...setExcluded,
                        [entry.key]: !setExcluded[entry.key],
                      })
                    }
                  />
                  <ChipV4 size="sm" tone={CATEGORY_TONES[entry.kind]}>
                    {CATEGORY_LABELS[entry.kind]}
                  </ChipV4>
                  <span className="hv4-ord-set-item-name">{entry.name}</span>
                  <span className="hv4-ord-set-item-price">
                    {formatComma(entry.price, "en-US", false)} ฿
                  </span>
                </label>
                <ButtonV4
                  variant="plain"
                  size="icon"
                  className="hv4-ord-set-remove"
                  aria-label={`ลบ ${entry.name} ออกจากชุด`}
                  title={`ลบ ${entry.name} ออกจากชุด`}
                  onClick={() => props.onRemoveSetEntry(item.id, entry.key)}
                >
                  <Trash2 size={18} aria-hidden="true" />
                </ButtonV4>
              </div>
            ))}
            <div className="hv4-ord-set-foot">
              <span className="hv4-ord-set-pick">
                เลือก {chosen.length} จาก {entries.length} รายการ
              </span>
              <ButtonV4
                variant="ok"
                disabled={chosen.length === 0}
                onClick={() => props.onAddSet(item.id, item.name)}
              >
                เพิ่มเข้าใบคำสั่ง
              </ButtonV4>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderSets = () => (
    <>
      <div role="tablist" aria-label="ประเภทชุด" className="hv4-ord-set-tabs">
        {SET_TABS.map(([value, label]) => (
          <ButtonV4
            key={value}
            variant="ghost"
            pill
            selected={props.setTab === value}
            role="tab"
            aria-selected={props.setTab === value}
            onClick={() => props.onChangeTab(value)}
          >
            {label}
          </ButtonV4>
        ))}
      </div>
      <div className="hv4-ord-set-list">
        {props.setRows.map(renderSet)}
        {props.setRows.length === 0 && (
          <div className="hv4-ord-panel-empty">ยังไม่มีชุดในกลุ่มนี้</div>
        )}
      </div>
    </>
  );

  const renderFavs = () => (
    <>
      <div className="hv4-ord-fav-list">
        {props.favourites.map((pick) => (
          <div key={pick.key} className="hv4-ord-fav-row">
            <button
              type="button"
              className="hv4-ord-fav-add"
              onClick={() => props.onAddFromFavourite(pick)}
            >
              <ChipV4 size="sm" tone={CATEGORY_TONES[pick.category]}>
                {CATEGORY_LABELS[pick.category]}
              </ChipV4>
              <b>{pick.name}</b>
              <span className="hv4-ord-fav-summary">
                {pick.summary ||
                  (pick.lab ? formatLabSummary({ urgency: "ROUTINE" }, pick.lab) : "")}
              </span>
            </button>
            <ButtonV4
              variant="plain"
              size="icon"
              aria-label={`เอา ${pick.name} ออกจากรายการโปรด`}
              title="เอาออกจากรายการโปรด"
              className="hv4-ord-fav-remove"
              onClick={() => props.onRemoveFavourite(pick)}
            >
              <Trash2 size={18} aria-hidden="true" />
            </ButtonV4>
          </div>
        ))}
        {props.favourites.length === 0 && (
          <div className="hv4-ord-panel-empty">ยังไม่มีรายการโปรด</div>
        )}
      </div>
      <div className="hv4-ord-picker">{props.favPicker}</div>
    </>
  );

  return (
    <ModalV4
      title={props.panel === "sets" ? "Order Set" : "★ รายการโปรด"}
      width={800}
      onClose={props.onClose}
    >
      {props.panel === "sets" ? renderSets() : renderFavs()}
    </ModalV4>
  );
};

ModalOrderSetV4.displayName = "ModalOrderSetV4";

export default ModalOrderSetV4;
